import { memo } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, List, Typography, useMediaQuery } from '@mui/material';

// project imports
import NavItem from './NavItem';
import NavGroup from './NavGroup';
import useConfig from 'hooks/useConfig';
import { useCurrentUser } from 'hooks/useUser.hooks';
import { useCurrentOrganization } from 'hooks/useOrganization.hooks';

import LAYOUT_CONST from 'constant';
import { HORIZONTAL_MAX_ITEM } from 'config';
import adminMenuItem from 'menu-items/admin';
import deliveryDriverMenuItem from 'menu-items/delivery-driver';
import donorMenuItem from 'menu-items/donor';
import recipientMenuItem from 'menu-items/recipient';
import superuserMenuItem from 'menu-items/superuser';
import superAdminMenuItem from 'menu-items/super-admin';
import { useSelector } from 'store';

// types
import { NavItemType } from 'types';

// ==============================|| SIDEBAR MENU LIST ||============================== //

const MenuList = () => {
    const theme = useTheme();
    const { layout } = useConfig();
    const { drawerOpen } = useSelector((state) => state.menu);
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    
    const userState = useCurrentUser();
    const organizationState = useCurrentOrganization();

	if (!userState.ready || !organizationState.ready) {
        return <div>Loading</div>;
    }
    const user = userState.user;
    const organization = organizationState.organization;

    var menuItem;
    if (user && user.role == 'Admin') {
      menuItem = adminMenuItem;
      
      if (!organization.messaging_enabled) {
        const partnerManagement = menuItem.items.find(item => item.id == 'partner-management')
        partnerManagement.children = partnerManagement.children.filter(child => child.id != 'messages');
      }
      if (!organization.marketplace_enabled) {
        const routesSection = menuItem.items.find(item => item.id === 'routes');
        routesSection.children = routesSection.children.filter(child => child.id !== 'marketplace-routes');
      }
      if (!organization.sharing_excess_enabled) {
        const organizationSection = menuItem.items.find(item => item.id === 'organizationSection');
        const integrationsSection = organizationSection.children.find(child => child.id === 'integrationsSection');
        integrationsSection.children = integrationsSection.children.filter(child => child.id !== 'sharing-excess-integration');
      }
    }
    else if (user && user.role == 'Delivery Driver')
    {
      menuItem = deliveryDriverMenuItem;
    }
    else if (user && user.role == 'Food Donor')
    {
      menuItem = donorMenuItem;
    }
    else if (user && user.role == 'Recipient')
    {
      menuItem = recipientMenuItem;

      if (!organization.marketplace_enabled) {
        menuItem.items = menuItem.items.filter(item => item.id !== 'marketplace');
      }
    }
    else if (user && user.role == 'Superuser')
    {
      menuItem = superuserMenuItem;
    }
    else if (user && user.role == 'Super Admin')
    {
      menuItem = superAdminMenuItem;
    }

    // last menu-item to show in horizontal menu bar
    const lastItem = layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd ? HORIZONTAL_MAX_ITEM : null;

    let lastItemIndex = menuItem.items.length - 1;
    let remItems: NavItemType[] = [];
    let lastItemId: string;

    if (lastItem && lastItem < menuItem.items.length) {
        lastItemId = menuItem.items[lastItem - 1].id!;
        lastItemIndex = lastItem - 1;
        remItems = menuItem.items.slice(lastItem - 1, menuItem.items.length).map((item) => ({
            title: item.title,
            elements: item.children,
            icon: item.icon,
            ...(item.url && {
                url: item.url
            })
        }));
    }

    const navItems = menuItem.items.slice(0, lastItemIndex + 1).map((item) => {
        switch (item.type) {
            case 'group':
                if (item.url && item.id !== lastItemId) {
                    return (
                        <List
                            key={item.id}
                            {...(drawerOpen && layout !== LAYOUT_CONST.HORIZONTAL_LAYOUT && { sx: { mt: 1.5 } })}
                            disablePadding={!drawerOpen || layout === LAYOUT_CONST.HORIZONTAL_LAYOUT}
                        >
                            <NavItem item={item} level={1} isParents />
                            {/* {layout !== LAYOUT_CONST.HORIZONTAL_LAYOUT && <Divider sx={{ py: 0.5 }} />} */}
                        </List>
                    );
                }

                return <NavGroup key={item.id} item={item} lastItem={lastItem!} remItems={remItems} lastItemId={lastItemId} />;
            default:
                return (
                    <Typography key={item.id} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    });

    return layout === LAYOUT_CONST.VERTICAL_LAYOUT || (layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && matchDownMd) ? (
        <Box {...(drawerOpen && { sx: { mt: 1.5 } })}>{navItems}</Box>
    ) : (
        <>{navItems}</>
    );
};

export default memo(MenuList);
